import { salesBomPartsMatching } from '@constants/URLs';
import { faFileSpreadsheet } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { SimpleSpinner } from 'src/components/Loading/SimpleSpinner';
import { Toast } from 'src/components/Toast';
import { device } from 'src/constants/mediaQueries';
import { useDecodedParams } from 'src/hooks/useDecodedParams';
import { useDownloadPartsCategoriesSpreadsheet } from 'src/hooks/useDownloadPartsCategories';
import { DownloadTemplateButton } from 'src/pages/CreateNewProductPage/styles';
import { useConfiguration } from 'src/providers/Configuration';
import { useTenant } from 'src/providers/Tenant';
import { useUser } from 'src/providers/User';
import { GA } from 'src/services/gtag';
import { LearnMorePartsCategoriesButton } from './LearnMorePartsCategoriesButton';

interface GTagEventData {
  user: string;
  account: string;
  product: string;
  role: string;
}

/* istanbul ignore next - not worth testing */
const learnMoreGTag = (eventData: GTagEventData) => {
  GA.gtag('event', 'manage_product_parts_categories_knowledgebase', eventData);
};

const downloadGTag = (eventData: GTagEventData) => {
  GA.gtag('event', 'manage_product_parts_categories_template_downloads', eventData);
};

export function DownloadPartsCategories() {
  const { parentPID: productPID } = useConfiguration();
  const location = useLocation();
  const { t } = useTranslation('manageProduct');
  const { accountId } = useTenant();
  const { productId } = useDecodedParams<{ productId: string }>();
  const isMobile = useMediaQuery(device.mobileOrTablet);
  const searchParams = new URLSearchParams(location?.search);
  const configurationPID = searchParams.get('configurationPID');
  const { username, role } = useUser();
  const {
    isLoading,
    isError,
    handleClick: handleDownloadClick,
  } = useDownloadPartsCategoriesSpreadsheet(accountId, configurationPID!, productPID);

  const eventData: GTagEventData = {
    user: username!,
    account: accountId,
    product: productId,
    role: role!,
  };

  return (
    <>
      <DownloadSectionContainer>
        <DownloadTemplateButton
          variant="contained"
          size={isMobile ? /* istanbul ignore next */ 'medium' : 'large'}
          startIcon={
            isLoading ? /* istanbul ignore next */ null : <FontAwesomeIcon icon={faFileSpreadsheet} size="3x" />
          }
          type="button"
          data-testid="download-categories-button"
          onClick={() => {
            handleDownloadClick();
            downloadGTag(eventData);
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            /* istanbul ignore next */ <SimpleSpinner />
          ) : (
            t('partsCategories.currentPartsCategories.downloadPartsCategoriesButton')
          )}
        </DownloadTemplateButton>
        {/* Temporary link until knowledge base updated */}
        <LearnMorePartsCategoriesButton
          onClick={/* istanbul ignore next - not worth testing */ () => learnMoreGTag(eventData)}
          href={salesBomPartsMatching}
        />
      </DownloadSectionContainer>
      <Toast
        dependency={isError}
        severity="error"
        title={`${t('partsCategories.currentPartsCategories.errorDownloadingPartsCategoriesSpreadsheetTitle')}`}
        message={`${t('partsCategories.currentPartsCategories.errorDownloadingPartsCategoriesSpreadsheetDescription')}`}
        data-testid="toast-notification"
      />
    </>
  );
}

const DownloadSectionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;
